/**
 * EntryPage — the one renderer for any codex entry (trophy/season/legend).
 * Same red-cover rhythm as the player page: hero band → lede → drop-cap
 * chapters → timeline → fact-file → a "Connected" rail of related entries →
 * a provenance line. Sanctioned fiction wears a visible label everywhere.
 */
import { DetailHeader, Rule, YearTag } from './chrome';
import { ShareButton } from './ShareButton';
import { entryLink } from '../lib/share';
import type { CodexEntry } from '../lib/types';

export const EntryPage = ({
  entry,
  onOpen,
  onBack,
  onOpenYear,
  backLabel = 'Back',
  trail,
}: {
  entry: CodexEntry;
  onOpen: (id: string) => void;
  onBack: () => void;
  /** Open the year view for a timeline year — every year is a doorway. */
  onOpenYear?: (year: string) => void;
  /** Label for the back button — names where this step returns to. */
  backLabel?: string;
  /** The full ancestor path for the "where am I" breadcrumb. */
  trail?: string[];
}) => {
  const fiction = entry.continuity === 'sanctioned-fiction';
  const v = entry.verification;
  const sourceUrls = [...new Set(entry.claims.flatMap((claim) => claim.sources).filter((source) => {
    try { return new URL(source).protocol === 'https:'; } catch { return false; }
  }))];
  return (
    <div className="screen detail-screen entry-screen">
      <DetailHeader
        onBack={onBack}
        backLabel={backLabel}
        trail={trail}
        right={
          <ShareButton
            ghost
            label="Share"
            moment={{
              title: `COYG — ${entry.title}`,
              text: `${entry.title}${entry.subtitle ? ` · ${entry.subtitle}` : ''} — ${entry.lede}`,
              url: entryLink(entry.id),
              card: {
                eyebrow: entry.subtitle ?? 'FROM THE VAULT',
                headline: entry.title,
                detail: entry.lede,
                stamp: 'THE VAULT · COYG.ORG',
              },
            }}
          />
        }
      />

      <header className={`player-head${fiction ? ' player-head--fiction' : ''}`}>
        {entry.hero?.stat && (
          <span className="player-head-num" aria-hidden="true">
            {entry.hero.stat}
          </span>
        )}
        {fiction && <span className="fiction-pill">Sanctioned fiction</span>}
        {entry.subtitle && <span className="player-kicker">{entry.subtitle}</span>}
        <h2 className="player-title">{entry.title}</h2>
      </header>
      <p className="player-lede">{entry.lede}</p>

      {entry.chapters.map((c) => (
        <article key={c.title} className="story-chapter">
          <Rule label={c.title} accent="var(--gold)" />
          <p className="story-text">{c.text}</p>
        </article>
      ))}

      {entry.timeline.length > 0 && (
        <>
          <Rule label="Timeline" />
          <ol className="story-timeline">
            {entry.timeline.map((t, i) => (
              <li key={`${t.year}-${i}`}>
                <YearTag year={t.year} onOpenYear={onOpenYear} />
                <span className="story-event">{t.event}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      {entry.facts.length > 0 && (
        <div className="fact-file">
          {entry.facts.map((f) => (
            <div key={f.label}>
              <em>{f.label}</em>
              <strong>{f.value}</strong>
            </div>
          ))}
        </div>
      )}

      {entry.links.length > 0 && (
        <>
          <Rule label="Connected" accent="#EF0107" />
          <div className="connected-rail">
            {entry.links.map((l) => (
              <button key={l.id} className="honour-chip honour-chip--link" onClick={() => onOpen(l.id)}>
                {l.label} <span aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        </>
      )}

      {sourceUrls.length > 0 && (
        <section className="entry-sources" aria-label="Source links">
          <Rule label="Sources" accent="var(--gold)" />
          <ul>{sourceUrls.map((source) => <li key={source}><a href={source} target="_blank" rel="noopener noreferrer">{new URL(source).hostname.replace(/^www\./, '')} ↗</a></li>)}</ul>
        </section>
      )}

      {v && (
        <p className="provenance">
          {fiction ? 'Sanctioned fiction' : 'Curated'}
          {v.verifiedAt
            ? ` · last verified ${new Date(v.verifiedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
            : ''}
          {v.sources ? ` · ${v.sources} source${v.sources === 1 ? '' : 's'}` : ''}
        </p>
      )}

    </div>
  );
};
